import { openDB, type DBSchema } from "idb";
import { StudyLease, type StudyStart, type StudyInterval } from "@ca/shared";
import { api, HttpError } from "./api.ts";
import { uuidv7 } from "./queue.ts";
export type StudyState = "recording" | "paused" | "offline" | "expired" | "revoked" | "error";
export interface StudyStatus {
  state: StudyState;
  message: string;
}
interface Job {
  key: string;
  uid: string;
  sessionId: string;
  interval: StudyInterval | null;
  end: boolean;
}
interface Store extends DBSchema {
  jobs: { key: string; value: Job };
}
export class StudyQueue {
  private store;
  private send: typeof api;
  private flight: Promise<StudyLease | null> | null = null;
  constructor(name = "ca-study-time", send: typeof api = api) {
    this.send = send;
    this.store = openDB<Store>(name, 1, {
      upgrade(db) {
        db.createObjectStore("jobs", { keyPath: "key" });
      },
    });
  }
  async put(uid: string, sessionId: string, interval: StudyInterval | null, end = false) {
    const key = `${uid}:${sessionId}:${interval ? interval.seq.toString().padStart(10, "0") : "end"}`;
    await (await this.store).put("jobs", { key, uid, sessionId, interval, end });
  }
  async pending(uid: string) {
    return (await (await this.store).getAll("jobs")).filter((x) => x.uid === uid);
  }
  async drain(
    uid: string,
    current: string | null = null,
    renew = false,
  ): Promise<StudyLease | null> {
    if (this.flight) {
      await this.flight;
      return this.drain(uid, current, renew);
    }
    const action = async () => {
      const db = await this.store,
        jobs = await this.pending(uid);
      let lease: StudyLease | null = null;
      for (const id of [
        ...new Set(jobs.map((x) => x.sessionId)),
        ...(current ? [current] : []),
      ].filter((id, i, a) => a.indexOf(id) === i)) {
        const group = jobs.filter((x) => x.sessionId === id),
          intervals = group
            .filter((x) => x.interval !== null)
            .sort((a, b) => (a.interval?.seq ?? 0) - (b.interval?.seq ?? 0));
        do {
          const batch = intervals.splice(0, 100);
          if (batch.length || id === current) {
            const result = StudyLease.parse(
              await this.send(`/v1/me/study-sessions/${id}/intervals`, {
                method: "POST",
                accountId: uid,
                body: { intervals: batch.map((x) => x.interval), renew: renew && id === current },
              }),
            );
            const tx = db.transaction("jobs", "readwrite");
            for (const j of batch) await tx.store.delete(j.key);
            await tx.done;
            if (id === current) lease = result;
          }
        } while (intervals.length);
        for (const j of group.filter((x) => x.end)) {
          await this.send(`/v1/me/study-sessions/${id}/end`, { method: "POST", accountId: uid });
          await db.delete("jobs", j.key);
        }
      }
      return lease;
    };
    this.flight = (
      typeof navigator !== "undefined" && typeof navigator.locks !== "undefined"
        ? navigator.locks.request(`ca-study-sync:${uid}`, action)
        : action()
    ).finally(() => {
      this.flight = null;
    });
    return this.flight;
  }
}
export const studyQueue = new StudyQueue();
const messages: Record<StudyState, string> = {
  recording: "Đang ghi giờ học",
  paused: "Giờ học tạm dừng",
  offline: "Offline: ghi giờ tối đa 10 phút từ nhịp online cuối",
  expired: "Đã hết 10 phút offline; bài làm vẫn được lưu",
  revoked: "Giờ học đang được ghi ở phiên khác",
  error: "Chưa kết nối để ghi giờ học",
};
export function studyStatus(state: StudyState) {
  if (typeof window !== "undefined")
    window.dispatchEvent(
      new CustomEvent<StudyStatus>("ca-study-status", {
        detail: { state, message: messages[state] },
      }),
    );
}
/** Pure time slicing shared by the timer and its tests. Hidden/idle time never becomes an interval. */
export function activitySlice(
  start: number,
  end: number,
  leaseEnd: number,
  lastInput: number,
  visible: boolean,
) {
  if (!visible) return null;
  const stop = Math.min(end, leaseEnd, lastInput + 180000),
    secs = Math.floor((stop - start) / 1000);
  return secs > 0
    ? {
        start: new Date(start).toISOString(),
        end: new Date(start + Math.min(60, secs) * 1000).toISOString(),
        activeSecs: Math.min(60, secs),
      }
    : null;
}
export class StudyTracker {
  private id = uuidv7();
  private seq = 0;
  private lease: StudyLease | null = null;
  private anchor = 0;
  private perf = 0;
  private cursor: number | null = null;
  private lastInput = 0;
  private stopped = false;
  private serial: Promise<void> = Promise.resolve();
  private timer: ReturnType<typeof setInterval> | null = null;
  private focused = true;
  private allowed = true;
  private opening = false;
  private uid: string;
  private input: StudyStart;
  private queue: StudyQueue;
  private send: typeof api;
  constructor(uid: string, input: StudyStart, queue = studyQueue, send: typeof api = api) {
    this.uid = uid;
    this.input = input;
    this.queue = queue;
    this.send = send;
  }
  private isStopped() {
    return this.stopped;
  }
  private time() {
    return this.anchor + (performance.now() - this.perf);
  }
  private publish(state: StudyState) {
    studyStatus(state);
  }
  private enqueue(action: () => Promise<void>) {
    this.serial = this.serial.then(action).catch(() => {
      if (!this.stopped) this.publish(navigator.onLine ? "error" : "offline");
    });
    return this.serial;
  }
  private canRecord() {
    return (
      !this.stopped &&
      this.allowed &&
      document.documentElement.dataset.focusPaused !== "true" &&
      this.focused &&
      !document.hidden &&
      !!this.lease &&
      !this.lease.revoked &&
      this.time() - this.lastInput < 180000
    );
  }
  private async open() {
    if (this.opening || this.lease || this.stopped) return;
    if (!navigator.onLine) {
      this.publish("error");
      return;
    }
    this.opening = true;
    try {
      await this.queue.drain(this.uid);
      const lease = StudyLease.parse(
        await this.send(`/v1/me/study-sessions/${this.id}`, {
          method: "PUT",
          accountId: this.uid,
          body: this.input,
        }),
      );
      this.lease = lease;
      this.anchor = Date.parse(lease.serverNow);
      this.perf = performance.now();
      this.lastInput = this.anchor;
      if (this.canRecord()) this.cursor = this.time();
      if (this.isStopped()) {
        await this.queue.put(this.uid, this.id, null, true);
        await this.queue.drain(this.uid);
        return;
      }
      this.publish(lease.revoked ? "revoked" : "recording");
    } finally {
      this.opening = false;
    }
  }
  start() {
    this.focused = document.hasFocus();
    void this.enqueue(() => this.open());
    for (const event of ["pointerdown", "keydown", "touchstart", "wheel"])
      window.addEventListener(event, this.interact, { passive: true });
    document.addEventListener("visibilitychange", this.visibility);
    window.addEventListener("blur", this.blur);
    window.addEventListener("focus", this.focus);
    window.addEventListener("online", this.online);
    window.addEventListener("offline", this.offline);
    window.addEventListener("pagehide", this.hide);
    window.addEventListener("ca-focus-fullscreen", this.focusScreen);
    this.timer = setInterval(() => {
      if (!this.lease) {
        if (navigator.onLine) void this.enqueue(() => this.open());
        return;
      }
      const t = this.time();
      if (
        this.cursor !== null &&
        (!this.canRecord() ||
          t - this.lastInput >= 180000 ||
          t - this.cursor >= 30000 ||
          t >= Date.parse(this.lease.expiresAt))
      )
        void this.flush();
      if (this.lease.revoked) this.publish("revoked");
      else if (t >= Date.parse(this.lease.expiresAt)) this.publish("expired");
      else if (!this.canRecord() || t - this.lastInput >= 180000) this.publish("paused");
      else this.publish(navigator.onLine ? "recording" : "offline");
    }, 1000);
  }
  private interact = () => {
    if (!this.lease) {
      void this.enqueue(() => this.open());
      return;
    }
    this.lastInput = this.time();
    if (
      navigator.onLine &&
      !this.lease.revoked &&
      this.lastInput >= Date.parse(this.lease.expiresAt)
    )
      void this.enqueue(() => this.sync());
    if (
      this.canRecord() &&
      this.cursor === null &&
      this.lastInput < Date.parse(this.lease.expiresAt)
    )
      this.cursor = this.lastInput;
  };
  private visibility = () => {
    if (document.hidden) {
      void this.flush(true);
    } else this.interact();
  };
  private blur = () => {
    this.focused = false;
    void this.flush(true);
  };
  private focus = () => {
    this.focused = true;
    this.interact();
  };
  private online = () => {
    void this.enqueue(async () => {
      if (!this.lease) await this.open();
      else await this.sync();
    });
  };
  private offline = () => {
    this.publish("offline");
  };
  private hide = () => {
    void this.flush(true);
  };
  private focusScreen = (event: Event) => {
    const e = event as CustomEvent<boolean>;
    this.allowed = !e.detail;
    if (!this.allowed) void this.flush(true);
    else if (this.canRecord() && this.cursor === null) this.cursor = this.time();
  };
  private async sync() {
    const current = await this.queue.drain(this.uid, this.id, this.canRecord());
    if (current) {
      // Keep the monotonic anchor while recording: network latency cannot create active time.
      this.lease = current;
      if (current.revoked) {
        this.cursor = null;
        this.publish("revoked");
      } else if (this.canRecord() && this.cursor === null) {
        this.anchor = Date.parse(current.serverNow);
        this.perf = performance.now();
        this.lastInput = this.anchor;
        this.cursor = this.anchor;
      }
      window.dispatchEvent(new Event("ca-study-ack"));
    }
  }
  flush(pause = false) {
    // Freeze timestamps before an async request; a visibility change cannot eat the previous slice.
    const lease = this.lease,
      start = this.cursor,
      end = lease ? this.time() : 0,
      last = this.lastInput;
    this.cursor =
      !pause &&
      this.canRecord() &&
      end - last < 180000 &&
      lease &&
      end < Date.parse(lease.expiresAt)
        ? end
        : null;
    const slice =
      lease && start !== null
        ? activitySlice(start, end, Date.parse(lease.expiresAt), last, true)
        : null;
    const interval = slice && lease ? { ...slice, seq: this.seq++, leaseId: lease.leaseId } : null;
    return this.enqueue(async () => {
      if (interval) await this.queue.put(this.uid, this.id, interval);
      if (lease && navigator.onLine) await this.sync();
    });
  }
  async settle() {
    await this.flush();
    await this.serial;
  }
  stop() {
    if (this.stopped) return;
    void this.flush(true);
    this.stopped = true;
    if (this.timer) clearInterval(this.timer);
    for (const event of ["pointerdown", "keydown", "touchstart", "wheel"])
      window.removeEventListener(event, this.interact);
    document.removeEventListener("visibilitychange", this.visibility);
    window.removeEventListener("blur", this.blur);
    window.removeEventListener("focus", this.focus);
    window.removeEventListener("online", this.online);
    window.removeEventListener("offline", this.offline);
    window.removeEventListener("pagehide", this.hide);
    window.removeEventListener("ca-focus-fullscreen", this.focusScreen);
    void this.enqueue(async () => {
      if (this.lease) {
        await this.queue.put(this.uid, this.id, null, true);
        if (navigator.onLine) await this.queue.drain(this.uid);
      }
    });
    this.publish("paused");
  }
}
export function permanentStudyError(error: unknown) {
  return (
    error instanceof HttpError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 401 &&
    error.status !== 429
  );
}
